import type { QueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { StatusCodes } from "http-status-codes";

/**
 * Union type for all possible error types that can occur in React Query
 */
export type QueryError = AxiosError | Error | string | null | undefined;

/**
 * Check if status code indicates authentication error
 */
const isAuthError = (statusCode: number): boolean => {
    return statusCode === StatusCodes.UNAUTHORIZED || statusCode === StatusCodes.FORBIDDEN;
};

/**
 * Determines if a query should be retried based on error type
 *
 * This function is needed to prevent React Query from retrying on 401/403 errors,
 * which are handled by our axios interceptors for automatic token refresh.
 * Without this, React Query would retry failed auth requests, creating conflicts
 * with our refresh token logic.
 *
 * @param failureCount - Number of previous failures
 * @param error - The error that occurred
 * @returns true if should retry, false otherwise
 */
export const shouldRetryQuery = (failureCount: number, error: QueryError): boolean => {
    // Don't retry on auth errors - let interceptor handle it
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status && isAuthError(status)) {
            return false;
        }
    }

    // Retry other errors up to 1 time
    return failureCount < 1;
};

/**
 * Invalidates only list queries for a given entity, keeping individual item details cached.
 * This is more efficient than invalidating all queries, as it preserves cached detail data.
 *
 * @param queryClient - The React Query client instance
 * @param entityName - The name of the entity (e.g., "treatments", "clinics", "staff", "shifts")
 *
 * @example
 * ```ts
 * invalidateListQueries(queryClient, "treatments");
 * // Invalidates ["treatments", "list", ...] queries
 * // Keeps ["treatments", "id"] queries cached
 * ```
 */
export const invalidateListQueries = (queryClient: QueryClient, entityName: string): void => {
    queryClient.invalidateQueries({
        queryKey: [entityName, "list"],
        exact: false,
    });
};
